import { useEffect, useState } from 'react'
import { Form, useFetcher, useFetchers, useNavigate } from 'react-router'
import { formatDate, STYLEPAGE_URL } from '@trinity/utils'
import {
  BackButton,
  Button,
  Check,
  ContentCopy,
  Delete,
  Dialog,
  Divider,
  Edit,
  ImageCards,
  IconButton,
  OpenInNew,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '../../components'
import { StyleflowRackCards } from './StyleflowRackCards'
import { useGlobalState } from '../../hooks'

interface StyleflowStylepageProps {
  stylepage: TrinityAPI.VisualizerStylepageType
  deleteConfirmation?: string
}

export function StyleflowStylepage({ stylepage, deleteConfirmation = undefined }: StyleflowStylepageProps) {
  const { setToastInfo } = useGlobalState()
  const navigate = useNavigate()
  const fetchers = useFetchers()
  const fetcher = fetchers.find(fetcher => fetcher.key === ImageCards.FETCHER_KEY)
  const styleflowRackCardsFetcher = fetchers.find(fetcher => fetcher.key === StyleflowRackCards.FETCHER_KEY)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const handleClose = () => setShowConfirmation(false)

  useEffect(() => {
    if (fetcher?.data?.message) {
      setToastInfo({ show: true, severity: 'success', message: fetcher?.data?.message })
    }
    if (styleflowRackCardsFetcher?.data?.message) {
      setToastInfo({ show: true, severity: 'success', message: styleflowRackCardsFetcher?.data?.message })
    }
  }, [fetcher?.data, styleflowRackCardsFetcher?.data, setToastInfo])

  useEffect(() => {
    if (deleteConfirmation) {
      setToastInfo({ show: true, severity: 'success', message: deleteConfirmation })
      navigate('/styleflow/stylepages')
    }
  }, [deleteConfirmation, navigate, setToastInfo])

  if (!stylepage) return null
  const shareUrl = `${STYLEPAGE_URL}shared/stylepages/${stylepage.uuid}`

  return (
    <Stack spacing={4}>
      <BackButton to='/styleflow/stylepages' />
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <TitleBar stylepage={stylepage} />
        <Button
          size='small'
          color='error'
          startIcon={<Delete />}
          onClick={() => setShowConfirmation(true)}
          sx={{ borderRadius: 5 }}
        >
          Delete Stylepage
        </Button>
      </Stack>
      <Typography variant='body3' sx={{ fontStyle: 'italic' }}>
        Last Edited: {formatDate(stylepage.updatedAt)}
      </Typography>
      <Button
        size='small'
        startIcon={<OpenInNew />}
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{ width: 'fit-content', borderRadius: 5 }}
      >
        Share Stylepage
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack direction='row' spacing={2} alignItems='center' p={2}>
          <Typography>{shareUrl}</Typography>
          <Tooltip placement='top' title='Copy Link'>
            <IconButton size='small' onClick={() => navigator.clipboard.writeText(shareUrl)}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Stack>
      </Popover>
      <Divider />
      <Typography variant='h3'>Items</Typography>
      <StyleflowRackCards
        items={stylepage.items}
        actions={[
          { type: 'add', title: 'Add', intent: 'add' },
          { type: 'edit', title: 'Edit', intent: 'edit' },
          { type: 'delete', title: 'Remove' },
        ]}
        showLinks
      />
      <Dialog open={showConfirmation} onClose={handleClose}>
        <Form method='POST'>
          <Stack spacing={4} p={4}>
            <Typography variant='h3' align='center'>
              Please Confirm
            </Typography>
            <Typography>This stylepage and all related items will be permanantly deleted.</Typography>
            <input type='hidden' name='id' value={stylepage.id} />
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button size='small' variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size='small'
                color='error'
                type='submit'
                name='intent'
                value='delete-stylepage'
                onClick={handleClose}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Dialog>
    </Stack>
  )
}

function TitleBar({ stylepage }: { stylepage: TrinityAPI.VisualizerStylepageType }) {
  const { setToastInfo } = useGlobalState()
  const fetcher = useFetcher()
  const [mode, setMode] = useState<'view' | 'edit'>('view')

  useEffect(() => {
    if (fetcher.data?.message) {
      setMode('view')
      setToastInfo({ show: true, severity: 'success', message: fetcher.data.message })
    }
  }, [fetcher.data, setToastInfo])

  if (!stylepage) return null

  if (mode === 'view') {
    return (
      <Stack direction='row' spacing={4}>
        <Typography variant='h2'>{stylepage.name}</Typography>
        <Tooltip placement='top' title='Edit Stylepage'>
          <IconButton onClick={() => setMode('edit')}>
            <Edit sx={{ bgcolor: 'neutral.extraLight' }} />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  if (mode === 'edit') {
    return (
      <fetcher.Form method='POST'>
        <Stack direction='row' spacing={4}>
          <TextField label='Stylepage Name' name='name' defaultValue={stylepage.name} />
          <input type='hidden' name='id' value={stylepage.id} />
          <Tooltip placement='top' title='Save Stylepage'>
            <IconButton type='submit' name='intent' value='edit-stylepage'>
              <Check sx={{ bgcolor: 'neutral.extraLight' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </fetcher.Form>
    )
  }

  return null
}
